import { useLazyQuery } from "@apollo/client";
import Button from "@mui/material/Button";
import { GridColDef } from "@mui/x-data-grid";
import { ClientAllocationRow } from "_graphql-types";
import { useMemo } from "react";
import { CLIENT_ALLOCATIONS } from "./graphql";
import { emptyFields, FLOW_KEYS, formatMonth, monthArg, totals } from "./logic";
import {
  EmptyState,
  FilterBar,
  groupCol,
  groupStarts,
  moneyCol,
  MonthField,
  Page,
  QueryState,
  ReportGrid,
  Section,
  textCol,
  TOTAL_ROW_ID,
  useSelectedMonth,
} from "./shared";

type Row = Pick<ClientAllocationRow, "rcgFundName" | "clientName" | "shareClassName" | (typeof FLOW_KEYS)[number]> & { id: number | string };

const flowColumns: GridColDef<Row>[] = [
  moneyCol<Row>("addition", "Addition"),
  moneyCol<Row>("redemption", "Redemption"),
  moneyCol<Row>("switchIn", "Switch In"),
  moneyCol<Row>("switchOut", "Switch Out"),
];

export function MonthlyCashFlowReport() {
  const [month, setMonth] = useSelectedMonth();
  const [load, { data, loading, error, called, variables }] = useLazyQuery(CLIENT_ALLOCATIONS, { fetchPolicy: "network-only" });

  const rows = useMemo((): Row[] => {
    const body: Row[] = (data?.clientAllocations ?? []).map(a => ({
      id: a.shareClassId,
      rcgFundName: a.rcgFundName,
      clientName: a.clientName,
      shareClassName: a.shareClassName,
      addition: a.addition,
      redemption: a.redemption,
      switchIn: a.switchIn,
      switchOut: a.switchOut,
    }));
    if (!body.length) return [];
    return [...body, { id: TOTAL_ROW_ID, rcgFundName: "Total", clientName: "", shareClassName: "", ...totals(body, FLOW_KEYS) }];
  }, [data]);
  const starts = useMemo(() => groupStarts(rows, r => r.rcgFundName), [rows]);
  // A flow nobody had this month is a column of blanks; leave it out.
  const shown = useMemo(() => {
    const empty = emptyFields(rows, FLOW_KEYS);
    return [
      groupCol<Row>("rcgFundName", "RCG Fund", starts, 260),
      textCol<Row>("clientName", "Client", 240),
      textCol<Row>("shareClassName", "Share Class", 140),
      ...(rows.length ? flowColumns.filter(c => !empty.has(c.field as (typeof FLOW_KEYS)[number])) : flowColumns),
    ];
  }, [rows, starts]);

  return (
    <Page title="Monthly Cash Flow Report" description="Every share class with an addition, redemption or switch in the month.">
      <FilterBar>
        <MonthField value={month} onChange={setMonth} />
        <Button variant="contained" onClick={() => load({ variables: { month: monthArg(month), withFlowsOnly: true } })}>
          Run report
        </Button>
      </FilterBar>
      <QueryState loading={loading} error={error} />
      {called && variables?.month ? (
        <Section title={`Cash flows, ${formatMonth(variables.month)}`}>
          <ReportGrid<Row> rows={rows} columns={shown} groupStarts={starts} noRowsLabel="No cash flows in this month" />
        </Section>
      ) : (
        <EmptyState>Pick a month and run the report.</EmptyState>
      )}
    </Page>
  );
}
